import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import { publicApi, DenemeBook } from '../../services/api';

const F = { fontFamily: 'Inter' } as const;

// Public, no-login catalog of "Deneme Sınavı" exam books. Reached from the
// Login screen's "Deneme Sınavı Videoları" button. Mirrors the logged-in
// Kütüphanem grid but is fed by the anonymous /public/deneme-books endpoint and
// only ever opens solution videos (no PDF, no progress).
export default function DenemeSinaviVideolari() {
  const navigate = useNavigate();
  const [books, setBooks] = useState<DenemeBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gradeFilter, setGradeFilter] = useState<string>('');   // grade_number as string
  const [subjectFilter, setSubjectFilter] = useState<string>(''); // subject name

  useEffect(() => {
    let alive = true;
    publicApi.denemeBooks()
      .then((r) => { if (alive) setBooks(r.data || []); })
      .catch((e) => { if (alive) setError(e?.response?.data?.error ?? 'Kitaplar yüklenemedi.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const grades = useMemo(() => {
    const map = new Map<number, string>();
    books.forEach((b) => { if (b.grade_levels) map.set(b.grade_levels.grade_number, b.grade_levels.name); });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]).map(([num, name]) => ({ num, name }));
  }, [books]);

  const subjects = useMemo(() => {
    const set = new Set<string>();
    books.forEach((b) => { if (b.subject?.name) set.add(b.subject.name); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [books]);

  const filtered = useMemo(() => books.filter((b) => {
    if (gradeFilter && String(b.grade_levels?.grade_number ?? '') !== gradeFilter) return false;
    if (subjectFilter && (b.subject?.name ?? '') !== subjectFilter) return false;
    return true;
  }), [books, gradeFilter, subjectFilter]);

  return (
    <div className="min-h-screen bg-[#f6f7fb]" style={F}>
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between gap-4">
          <BrandLogo variant="full" className="h-[34px] lg:h-[40px] object-contain" />
          <button
            onClick={() => navigate('/login')}
            className="text-[14px] lg:text-[15px] font-semibold text-[#3973bf] hover:underline"
          >
            Giriş Yap
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 lg:px-8 py-7">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-[22px] lg:text-[28px] font-semibold text-[#2b2b2b]">Deneme Sınavı Videoları</h1>
            <p className="text-[13px] lg:text-[15px] text-[#888] mt-1">
              Deneme sınavlarının soru çözüm videolarını ücretsiz izleyin — giriş yapmanıza gerek yok.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {grades.length > 1 && (
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="bg-white rounded-[12px] px-4 py-2.5 text-[14px] font-medium text-[#2b2b2b] border border-gray-200 outline-none"
              >
                <option value="">Tüm Sınıflar</option>
                {grades.map((g) => <option key={g.num} value={String(g.num)}>{g.name}</option>)}
              </select>
            )}
            {subjects.length > 0 && (
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                className="bg-white rounded-[12px] px-4 py-2.5 text-[14px] font-medium text-[#2b2b2b] border border-gray-200 outline-none"
              >
                <option value="">Tüm Dersler</option>
                {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <p className="text-center text-red-500 py-20">{error}</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-400 py-20">Deneme sınavı bulunamadı.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5">
            {filtered.map((b, i) => (
              <DenemeCard key={b.id} book={b} index={i} onClick={() => navigate(`/deneme-sinavi-videolari/${b.id}`)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function DenemeCard({ book, index, onClick }: { book: DenemeBook; index: number; onClick: () => void }) {
  const cover = book.cover_url || `/a-book${(index % 5) + 1}.png`;
  return (
    <button
      onClick={onClick}
      className="text-left bg-white rounded-[18px] p-3 border border-gray-100 hover:shadow-md transition-shadow flex flex-col"
    >
      <div className="w-full aspect-[3/4] rounded-[12px] overflow-hidden bg-gray-100 mb-3">
        <img
          src={cover}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = `/a-book${(index % 5) + 1}.png`; }}
        />
      </div>
      <div className="flex-1 flex flex-col">
        <p className="text-[14px] lg:text-[15px] font-medium text-[#2b2b2b] line-clamp-2 leading-snug">{book.display_name || book.title}</p>
        <p className="text-[12px] text-[#888] mt-1">
          {book.grade_levels?.name}{book.subject?.name ? ` · ${book.subject.name}` : ''}
        </p>
        <span className="mt-2 inline-flex items-center gap-1 self-start rounded-full bg-[#fdecf4] text-[#e5599b] text-[11px] font-semibold px-2.5 py-1">
          ▶ Video Çözümlü
        </span>
      </div>
    </button>
  );
}
